// LAYOUT-SPEC §0 — one deliverable across periods (wireframe 4)
//
//   ONE QUESTION  What does the contract say about this deliverable, who owes
//                 it in each window, and how is each date doing?
//   PRIMARY OBJECT  One duty (Archetype B + C: the dates table opens the date
//                 drawer).
//   PRIMARY VERB    Edit rule (the fix for "needs a start date"); + Add a date.
//   WORST-CASE VOLUME  Monthly across five periods and a few teams: ~120 dates.
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraActionList, SpecraBadge, SpecraBreadcrumbs, SpecraButton, SpecraDateField, SpecraDetailPage, SpecraEmptyState, SpecraFilterToolbar, SpecraFormField, SpecraInlineBanner, SpecraInput, SpecraKeyValueRow, SpecraLink, SpecraPageHeader, SpecraSectionHeader, SpecraSelect, SpecraText } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { AlertTriangle } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PageBody } from '../../../../components/layout';
import { useProgramContext } from '../../../../layouts/ProgramLayout';
import { apiErrorMessage } from '../../../../lib/api/apiErrorMessage';
import { renderRouterLink } from '../../../../lib/renderRouterLink';
import { ConfirmDeleteDialog } from '../../_shared/components/ConfirmDeleteDialog';
import { ErrorState } from '../../_shared/components/PageStates';
import { useCmAccess } from '../access';
import { AwaitingApprovalBanner } from '../components/AwaitingApproval';
import { CmDrawer } from '../components/CmDrawer';
import { CmAwaitingApproval, CmSourceButton, CmStatusBadge, CmTable } from '../components/common';
import { LogEventDrawer } from '../components/LogEventDrawer';
import { AroSuggestion, RuleEditorDrawer } from '../components/RuleEditor';
import { useCmDrawerParams } from '../drawerParams';
import { OccurrenceWindow, useAddOccurrence, useDeleteDuty, useDuty, useOccurrences, useWorkstreamDetails } from '../hooks';
import { cmDate, cmSpan } from '../lib/format';
import { describeRule, eventLabel, mintReceipt } from '../lib/rule';
import { HANDLING_LABEL, NOT_OWED_REASON_LABEL, occurrenceStatus, tierLabel } from '../lib/status';
import { useCmLookups } from '../lookups';
import { DutyFormDrawer } from './DeliverablesPage';
const FIX_LABEL = {
    UpdateScheduleRule: 'Edit the rule',
    CreateScheduleRule: 'Add a rule',
    LogContractEvent: 'Log the event date',
    UpdateDeliverableSettings: 'Open program settings',
    SetDutyAssignment: 'Open the team',
    RuleSowGap: 'Open the team',
    UpdateDuty: 'Edit the deliverable'
};
function summarize(occs) {
    const by = new Map();
    for (const o of occs)by.set(o.status?.state ?? '', (by.get(o.status?.state ?? '') ?? 0) + 1);
    const order = [
        'late',
        'rejected',
        'awaiting_review',
        'due_soon',
        'upcoming',
        'waiting_on_event',
        'held',
        'approved',
        'waived',
        'not_owed',
        'due_unknown'
    ];
    const word = {
        late: 'late',
        rejected: 'rejected',
        awaiting_review: 'awaiting review',
        due_soon: 'due soon',
        upcoming: 'upcoming',
        waiting_on_event: 'projected',
        held: 'held',
        approved: 'approved',
        waived: 'waived',
        not_owed: 'not owed',
        due_unknown: 'due date unknown'
    };
    return order.filter((s)=>by.get(s)).map((s)=>`${by.get(s)} ${word[s]}`).join(', ');
}
// checkRowId keys a check row. The message is part of it: a duty the award
// schedules in several periods the contract lacks carries one
// period_not_stated check per period, alike in every other field (SAD-775).
export function checkRowId(c) {
    return `${c.code}-${c.ruleId}-${c.workstreamId}-${c.periodOfPerformanceId}-${c.exerciseId}-${c.fromOn}-${c.message}`;
}
// A check's fix target: the rule or team its fix opens. Checks that differ
// only in what they affect (one per exercise, say) share a target and a row;
// checks whose fixes open different rules or teams never do.
function fixTarget(c) {
    switch(c.fixVerb){
        case 'UpdateScheduleRule':
        case 'CreateScheduleRule':
            return c.ruleId;
        case 'SetDutyAssignment':
        case 'RuleSowGap':
            return c.workstreamId;
        default:
            return '';
    }
}
/** groupDutyChecks folds identical checks (same message and fix) into one row. */ export function groupDutyChecks(checks) {
    const groups = new Map();
    for (const c of checks){
        const message = c.message || c.code.replace(/_/g, ' ');
        const key = `${c.code}|${c.fixVerb}|${fixTarget(c)}|${message}`;
        const g = groups.get(key) ?? {
            key,
            message,
            checks: []
        };
        g.checks.push(c);
        groups.set(key, g);
    }
    return [
        ...groups.values()
    ];
}
/** checkAffects names what one check of a group is about. */ export function checkAffects(c, lk) {
    const ex = lk.exerciseById.get(c.exerciseId);
    const ot = ex ? lk.optionalTaskById.get(ex.optionalTaskId) : undefined;
    const pop = lk.periodById.get(c.periodOfPerformanceId);
    const where = ex ? `${ot?.short ? `${ot.short} ` : ''}exercise ${ex.clinNumber} (${cmSpan(ex.start, ex.end)})` : pop ? `${pop.name} (${cmSpan(pop.start, pop.end)})` : c.fromOn ? cmSpan(c.fromOn, c.toOn) : '';
    const team = c.workstreamId ? lk.teamName(c.workstreamId) : '';
    return [
        team,
        where
    ].filter(Boolean).join(' · ') || 'The whole deliverable';
}
export function windowGroups(occs, lk) {
    const groups = new Map();
    for (const o of occs){
        const ex = lk.exerciseById.get(o.exerciseId);
        const pop = lk.periodById.get(o.periodOfPerformanceId);
        const key = ex ? `ex:${ex.start}:${ex.end}` : pop ? `pop:${pop.id}` : 'other';
        const label = ex ? `Window ${cmSpan(ex.start, ex.end)}` : pop ? `${pop.name} · ${cmSpan(pop.start, pop.end)}` : 'Added by hand';
        const g = groups.get(key) ?? {
            label,
            start: ex?.start ?? pop?.start ?? '9',
            byTeam: new Map()
        };
        const teamKey = `${o.workstreamId}|${o.exerciseId}`;
        const arr = g.byTeam.get(teamKey) ?? [];
        arr.push(o);
        g.byTeam.set(teamKey, arr);
        groups.set(key, g);
    }
    return [
        ...groups.entries()
    ].sort((a, b)=>a[1].start.localeCompare(b[1].start)).map(([key, g])=>({
            key,
            label: g.label,
            lines: [
                ...g.byTeam.entries()
            ].map(([k, list])=>{
                const [ws, exId] = k.split('|');
                return {
                    // The grouping's own ids: two lines can read alike (an unloaded
                    // team, an exercise with no CLIN) and still be two lines.
                    key: k,
                    team: lk.teamName(ws),
                    exercise: lk.exerciseById.get(exId)?.clinNumber ?? '',
                    count: list.length,
                    summary: summarize(list)
                };
            })
        }));
}
/** The "waiting on <event>" banner's line, from the check's planned date. */ export function waitingLine(c, event) {
    return c.fromOn ? `Waiting on ${event}. Its dates are projected from the planned ${cmDate(c.fromOn)} until the event is logged, and are not late while they wait.` : `Waiting on ${event}. No dates until the event is logged.`;
}
export function DutyPage() {
    const { dutyId = '' } = useParams();
    const { programId } = useProgramContext();
    const access = useCmAccess(programId);
    const params = useCmDrawerParams();
    const lk = useCmLookups(programId);
    const duty = useDuty(programId, dutyId);
    const dates = useOccurrences(programId, {
        dutyId,
        window: OccurrenceWindow.ALL,
        includeProjected: true
    });
    const teams = useWorkstreamDetails(programId, lk.workstreams.map((w)=>w.id));
    const [editRule, setEditRule] = useState(null);
    const [editDuty, setEditDuty] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const navigate = useNavigate();
    const deleteDuty = useDeleteDuty(programId, {
        success: 'Deliverable deleted',
        failure: 'Could not delete'
    });
    const [addDate, setAddDate] = useState(false);
    const [logKind, setLogKind] = useState(null);
    const [receipt, setReceipt] = useState(null);
    const [teamFilter, setTeamFilter] = useState('');
    const [familyFilter, setFamilyFilter] = useState('');
    const [expandedChecks, setExpandedChecks] = useState(new Set());
    const d = duty.data?.duty;
    const rules = duty.data?.rules ?? [];
    const checks = duty.data?.checks ?? [];
    const assignments = duty.data?.assignments ?? [];
    const occs = useMemo(()=>[
            ...dates.data?.occurrences ?? []
        ].sort((a, b)=>(a.dueOn || '9').localeCompare(b.dueOn || '9')), [
        dates.data
    ]);
    // A team's family: itself plus every predecessor, walking the lineage.
    const family = useMemo(()=>{
        if (!familyFilter) return null;
        const preds = new Map();
        lk.workstreams.forEach((w, i)=>{
            preds.set(w.id, (teams[i]?.data?.predecessors ?? []).map((p)=>p.predecessorWorkstreamId));
        });
        const out = new Set();
        const walk = (id)=>{
            if (out.has(id)) return;
            out.add(id);
            for (const p of preds.get(id) ?? [])walk(p);
        };
        walk(familyFilter);
        return out;
    }, [
        familyFilter,
        lk.workstreams,
        teams
    ]);
    const shown = occs.filter((o)=>(!teamFilter || o.workstreamId === (teamFilter === 'contract-wide' ? '' : teamFilter)) && (!family || family.has(o.workstreamId)));
    if (duty.error) {
        return /*#__PURE__*/ _jsx(PageBody, {
            children: /*#__PURE__*/ _jsx(ErrorState, {
                error: duty.error,
                title: "Could not load this deliverable",
                onRetry: ()=>void duty.refetch()
            })
        });
    }
    const needsStart = checks.filter((c)=>c.code === 'needs_start_date');
    const waiting = checks.filter((c)=>c.code === 'waiting_on_event');
    const otherChecks = checks.filter((c)=>c.code !== 'needs_start_date' && c.code !== 'waiting_on_event');
    const kindOfRule = (ruleId)=>rules.find((r)=>r.id === ruleId)?.eventKind ?? '';
    const eventName = (kind)=>eventLabel(kind, lk.duties.find((x)=>x.eventKind === kind)?.title);
    const checkGroups = groupDutyChecks(otherChecks);
    const fix = (c)=>{
        switch(c.fixVerb){
            case 'UpdateScheduleRule':
            case 'CreateScheduleRule':
                {
                    const r = rules.find((x)=>x.id === c.ruleId);
                    setEditRule(r ?? 'new');
                    return;
                }
            case 'UpdateDuty':
                setEditDuty(true);
                return;
            default:
        }
    };
    const fixLink = (c)=>{
        if (c.fixVerb === 'UpdateDeliverableSettings') return `/delivery/programs/${programId}/settings`;
        if ((c.fixVerb === 'SetDutyAssignment' || c.fixVerb === 'RuleSowGap') && c.workstreamId) return `/delivery/programs/${programId}/workstreams/${c.workstreamId}`;
        return null;
    };
    const columns = [
        {
            id: 'due',
            header: 'Due',
            cell: (o)=>/*#__PURE__*/ _jsxs("span", {
                    className: "tabular-nums",
                    children: [
                        cmDate(o.dueOn, 'Not stated'),
                        o.label ? /*#__PURE__*/ _jsx("span", {
                            className: "ml-2 text-muted-foreground",
                            children: o.label
                        }) : null,
                        o.event?.projected ? /*#__PURE__*/ _jsx(SpecraBadge, {
                            variant: "info",
                            className: "ml-2",
                            "data-testid": Testids.cmDuty.projectedBadge,
                            children: "Projected"
                        }) : null
                    ]
                })
        },
        {
            id: 'team',
            header: 'Team',
            cell: (o)=>lk.teamName(o.workstreamId)
        },
        {
            id: 'latest',
            header: 'Latest submission',
            cell: (o)=>o.latestSubmission ? /*#__PURE__*/ _jsxs("span", {
                    children: [
                        "#",
                        o.latestSubmission.attempt,
                        " ",
                        cmDate(o.latestSubmission.submittedOn, ''),
                        /*#__PURE__*/ _jsxs("span", {
                            className: "text-muted-foreground",
                            children: [
                                ' ',
                                "(",
                                o.latestSubmission.attachments.map((a)=>a.url ? 'link' : 'file').join(' + '),
                                ")"
                            ]
                        })
                    ]
                }) : /*#__PURE__*/ _jsx("span", {
                    className: "text-muted-foreground",
                    children: "—"
                })
        },
        {
            id: 'status',
            header: 'Status',
            cell: (o)=>/*#__PURE__*/ _jsxs("span", {
                    className: "inline-flex flex-wrap items-center gap-1",
                    children: [
                        /*#__PURE__*/ _jsx(CmStatusBadge, {
                            status: occurrenceStatus(o, d?.finalReviewTier)
                        }),
                        /*#__PURE__*/ _jsx(CmAwaitingApproval, {
                            programId: programId,
                            occurrenceId: o.id
                        })
                    ]
                })
        }
    ];
    const source = d ? `${d.origin === 'sow' ? 'SOW deliverable' : d.origin === 'manual' ? 'Added by hand' : 'Award'}${d.pwsRef ? ` PWS ${d.pwsRef}` : ''}${d.letter ? ` deliverable ${d.letter}` : ''}` : '';
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsxs(SpecraDetailPage, {
                "data-testid": Testids.cmDuty.page,
                header: /*#__PURE__*/ _jsxs("div", {
                    className: "space-y-4",
                    children: [
                        /*#__PURE__*/ _jsx(SpecraBreadcrumbs, {
                            renderLink: renderRouterLink,
                            items: [
                                {
                                    id: 'deliverables',
                                    label: 'Deliverables',
                                    href: `/delivery/programs/${programId}/deliverables`
                                },
                                {
                                    id: 'duty',
                                    label: d?.title ?? 'Deliverable',
                                    current: true
                                }
                            ]
                        }),
                        /*#__PURE__*/ _jsx(SpecraPageHeader, {
                            titleSize: "home",
                            title: d?.title ?? 'Deliverable',
                            actions: access.canWrite && d ? /*#__PURE__*/ _jsxs("div", {
                                className: "flex gap-2",
                                children: [
                                    d.eventKind ? /*#__PURE__*/ _jsxs(SpecraButton, {
                                        variant: "secondary",
                                        onClick: ()=>setLogKind(d.eventKind),
                                        "data-testid": Testids.cmDuty.logEvent,
                                        children: [
                                            "Log ",
                                            eventLabel(d.eventKind, d.title)
                                        ]
                                    }) : null,
                                    /*#__PURE__*/ _jsx(SpecraButton, {
                                        variant: "secondary",
                                        onClick: ()=>setEditDuty(true),
                                        children: "Edit deliverable"
                                    }),
                                    /*#__PURE__*/ _jsx(SpecraButton, {
                                        variant: "destructive",
                                        onClick: ()=>setConfirmDelete(true),
                                        "data-testid": Testids.cmDuty.delete,
                                        children: "Delete deliverable"
                                    }),
                                    /*#__PURE__*/ _jsx(SpecraButton, {
                                        onClick: ()=>params.open('submit', 'new', {
                                                forDuty: d.id
                                            }),
                                        children: "+ Submit"
                                    })
                                ]
                            }) : undefined
                        })
                    ]
                }),
                aside: d ? /*#__PURE__*/ _jsxs("div", {
                    "data-testid": Testids.cmDuty.header,
                    children: [
                        /*#__PURE__*/ _jsx(SpecraKeyValueRow, {
                            label: "Source",
                            value: /*#__PURE__*/ _jsxs("span", {
                                className: "inline-flex flex-wrap items-center justify-end gap-1.5",
                                children: [
                                    source,
                                    /*#__PURE__*/ _jsx(CmSourceButton, {
                                        locator: d.sourceLocator
                                    })
                                ]
                            })
                        }),
                        d.sourceLocator ? /*#__PURE__*/ _jsx(SpecraKeyValueRow, {
                            label: "Where",
                            value: d.sourceLocator
                        }) : null,
                        /*#__PURE__*/ _jsx(SpecraKeyValueRow, {
                            label: "Final review",
                            value: tierLabel(d.finalReviewTier)
                        }),
                        d.eventKind ? /*#__PURE__*/ _jsx(SpecraKeyValueRow, {
                            label: "Dates the event",
                            value: `${eventLabel(d.eventKind)}: met when it is logged`
                        }) : null,
                        d.endedOn ? /*#__PURE__*/ _jsx(SpecraKeyValueRow, {
                            "data-testid": Testids.cmDuty.ended,
                            label: "Ended",
                            value: `${cmDate(d.endedOn)}; no dates after it`
                        }) : null,
                        d.acceptanceCriteria ? /*#__PURE__*/ _jsx(SpecraKeyValueRow, {
                            label: "Acceptance",
                            value: d.acceptanceCriteria
                        }) : null
                    ]
                }) : undefined,
                children: [
                    /*#__PURE__*/ _jsx(AwaitingApprovalBanner, {
                        programId: programId,
                        duty: dutyId
                    }),
                    receipt ? /*#__PURE__*/ _jsxs(SpecraInlineBanner, {
                        tone: "success",
                        variant: "framed",
                        "data-testid": Testids.cmDuty.ruleReceipt,
                        onDismiss: ()=>setReceipt(null),
                        children: [
                            "Rule saved: ",
                            receipt,
                            "."
                        ]
                    }) : null,
                    /*#__PURE__*/ _jsxs("section", {
                        className: "space-y-2",
                        children: [
                            /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                                variant: "group",
                                title: "Rule",
                                action: access.canWrite && rules.length > 0 ? /*#__PURE__*/ _jsx(SpecraButton, {
                                    size: "sm",
                                    variant: "secondary",
                                    onClick: ()=>setEditRule('new'),
                                    "data-testid": Testids.cmDuty.addRule,
                                    children: "Add rule"
                                }) : undefined
                            }),
                            rules.length === 0 && !duty.isLoading ? /*#__PURE__*/ _jsx("div", {
                                "data-testid": Testids.cmDuty.noRules,
                                children: /*#__PURE__*/ _jsx(SpecraEmptyState, {
                                    framed: true,
                                    title: "Add the rule the award states",
                                    description: "Dates come from a rule, e.g. “Due seven days after Optional Task Exercise and monthly thereafter.” Without one, nothing is scheduled.",
                                    action: access.canWrite ? /*#__PURE__*/ _jsx(SpecraButton, {
                                        onClick: ()=>setEditRule('new'),
                                        "data-testid": Testids.cmDuty.addRule,
                                        children: "Add rule"
                                    }) : undefined
                                })
                            }) : rules.map((r)=>/*#__PURE__*/ _jsxs("div", {
                                    className: "space-y-1 border-l-2 border-border pl-3 text-[length:var(--fs-ui-body)]",
                                    "data-testid": Testids.cmDuty.ruleBlock,
                                    "data-row-id": r.id,
                                    children: [
                                        r.ruleText ? /*#__PURE__*/ _jsxs("blockquote", {
                                            className: "italic",
                                            "data-testid": Testids.cmDuty.ruleText,
                                            children: [
                                                "“",
                                                r.ruleText,
                                                "”"
                                            ]
                                        }) : null,
                                        /*#__PURE__*/ _jsxs("div", {
                                            className: "flex flex-wrap items-center gap-2",
                                            children: [
                                                /*#__PURE__*/ _jsxs("span", {
                                                    "data-testid": Testids.cmDuty.ruleReading,
                                                    children: [
                                                        "→ ",
                                                        describeRule(r)
                                                    ]
                                                }),
                                                /*#__PURE__*/ _jsx(CmSourceButton, {
                                                    locator: r.sourceLocator,
                                                    quote: r.ruleText
                                                }),
                                                access.canWrite ? /*#__PURE__*/ _jsx(SpecraButton, {
                                                    size: "sm",
                                                    variant: "ghost",
                                                    onClick: ()=>setEditRule(r),
                                                    "data-testid": Testids.cmDuty.ruleEdit,
                                                    children: "Edit rule"
                                                }) : null
                                            ]
                                        })
                                    ]
                                }, r.id))
                        ]
                    }),
                    needsStart.map((c)=>/*#__PURE__*/ _jsx(SpecraInlineBanner, {
                            tone: "warning",
                            variant: "framed",
                            "data-testid": Testids.cmDuty.needsStart,
                            action: !access.canWrite ? undefined : c.fixVerb === 'LogContractEvent' ? /*#__PURE__*/ _jsxs(SpecraButton, {
                                size: "sm",
                                variant: "secondary",
                                onClick: ()=>setLogKind(kindOfRule(c.ruleId)),
                                "data-testid": Testids.cmDuty.logEvent,
                                children: [
                                    "Log ",
                                    eventName(kindOfRule(c.ruleId))
                                ]
                            }) : /*#__PURE__*/ _jsx(SpecraButton, {
                                size: "sm",
                                variant: "secondary",
                                onClick: ()=>fix(c),
                                "data-testid": Testids.cmDuty.checkFix,
                                children: "Set the first date"
                            }),
                            children: /*#__PURE__*/ _jsxs("div", {
                                className: "space-y-1.5",
                                children: [
                                    /*#__PURE__*/ _jsxs("div", {
                                        children: [
                                            "No dates yet: this rule has no first date",
                                            c.message ? ` (${c.message})` : '',
                                            "."
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsx(AroSuggestion, {
                                        programId: programId,
                                        check: c,
                                        canWrite: access.canWrite
                                    })
                                ]
                            })
                        }, `${c.ruleId}-${c.periodOfPerformanceId}-${c.exerciseId}`)),
                    waiting.map((c)=>/*#__PURE__*/ _jsx(SpecraInlineBanner, {
                            tone: "info",
                            variant: "framed",
                            "data-testid": Testids.cmDuty.waitingOnEvent,
                            "data-row-id": kindOfRule(c.ruleId),
                            action: access.canWrite ? /*#__PURE__*/ _jsxs(SpecraButton, {
                                size: "sm",
                                variant: "secondary",
                                onClick: ()=>setLogKind(kindOfRule(c.ruleId)),
                                "data-testid": Testids.cmDuty.logEvent,
                                children: [
                                    "Log ",
                                    eventName(kindOfRule(c.ruleId))
                                ]
                            }) : undefined,
                            children: waitingLine(c, eventName(kindOfRule(c.ruleId)))
                        }, checkRowId(c))),
                    checkGroups.length > 0 ? /*#__PURE__*/ _jsxs("section", {
                        className: "space-y-2",
                        "data-testid": Testids.cmDuty.checks,
                        children: [
                            /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                                variant: "group",
                                title: /*#__PURE__*/ _jsxs(_Fragment, {
                                    children: [
                                        "Checks · ",
                                        checkGroups.length
                                    ]
                                })
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: "divide-y divide-border",
                                children: checkGroups.map((group)=>{
                                    const c = group.checks[0];
                                    const count = group.checks.length;
                                    const link = fixLink(c);
                                    const canFix = !link && access.canWrite && !!FIX_LABEL[c.fixVerb];
                                    const fixLabel = link ? FIX_LABEL[c.fixVerb] ?? 'Fix' : FIX_LABEL[c.fixVerb];
                                    const expanded = expandedChecks.has(group.key);
                                    const toggle = ()=>setExpandedChecks((prev)=>{
                                            const next = new Set(prev);
                                            if (!next.delete(group.key)) next.add(group.key);
                                            return next;
                                        });
                                    // A repeated check carries its own controls, so the row itself
                                    // is not a button: the expander and the fix sit side by side.
                                    const item = count === 1 ? {
                                        id: checkRowId(c),
                                        icon: /*#__PURE__*/ _jsx(AlertTriangle, {
                                            "aria-hidden": true
                                        }),
                                        tone: 'warning',
                                        label: group.message,
                                        href: link ?? undefined,
                                        onClick: canFix ? ()=>fix(c) : undefined,
                                        meta: link || canFix ? /*#__PURE__*/ _jsx("span", {
                                            "data-testid": Testids.cmDuty.checkFix,
                                            children: fixLabel
                                        }) : undefined
                                    } : {
                                        id: group.key,
                                        icon: /*#__PURE__*/ _jsx(AlertTriangle, {
                                            "aria-hidden": true
                                        }),
                                        tone: 'warning',
                                        label: group.message,
                                        meta: `${count}×`,
                                        actions: /*#__PURE__*/ _jsxs(_Fragment, {
                                            children: [
                                                /*#__PURE__*/ _jsx(SpecraButton, {
                                                    size: "inline",
                                                    variant: "link",
                                                    "aria-expanded": expanded,
                                                    onClick: toggle,
                                                    "data-testid": Testids.cmDuty.checkExpand,
                                                    children: expanded ? 'Hide' : `Show all ${count}`
                                                }),
                                                link ? /*#__PURE__*/ _jsx(SpecraLink, {
                                                    renderLink: renderRouterLink,
                                                    href: link,
                                                    "data-testid": Testids.cmDuty.checkFix,
                                                    children: fixLabel
                                                }) : canFix ? /*#__PURE__*/ _jsx(SpecraButton, {
                                                    size: "inline",
                                                    variant: "link",
                                                    onClick: ()=>fix(c),
                                                    "data-testid": Testids.cmDuty.checkFix,
                                                    children: fixLabel
                                                }) : null
                                            ]
                                        })
                                    };
                                    return /*#__PURE__*/ _jsxs("div", {
                                        children: [
                                            /*#__PURE__*/ _jsx(SpecraActionList, {
                                                renderLink: renderRouterLink,
                                                getItemTestId: ()=>Testids.cmDuty.checkRow,
                                                items: [
                                                    item
                                                ]
                                            }),
                                            count > 1 && expanded ? /*#__PURE__*/ _jsx("ul", {
                                                className: "space-y-1 pb-3 pl-12",
                                                "data-testid": Testids.cmDuty.checkAffected,
                                                children: group.checks.map((x)=>/*#__PURE__*/ _jsx("li", {
                                                        children: /*#__PURE__*/ _jsx(SpecraText, {
                                                            size: "caption",
                                                            tone: "muted",
                                                            children: checkAffects(x, lk)
                                                        })
                                                    }, checkRowId(x)))
                                            }) : null
                                        ]
                                    }, group.key);
                                })
                            })
                        ]
                    }) : null,
                    /*#__PURE__*/ _jsxs("section", {
                        className: "space-y-2",
                        children: [
                            /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                                variant: "group",
                                title: "Who owes it, by exercise"
                            }),
                            occs.length === 0 && assignments.length === 0 ? /*#__PURE__*/ _jsx(SpecraText, {
                                as: "p",
                                size: "caption",
                                tone: "muted",
                                children: "No one owes it yet: dates appear once the rule has a start and its period or exercise is on the contract."
                            }) : /*#__PURE__*/ _jsxs("div", {
                                className: "space-y-3",
                                children: [
                                    windowGroups(occs, lk).map((g)=>/*#__PURE__*/ _jsxs("div", {
                                            "data-testid": Testids.cmDuty.windowGroup,
                                            className: "text-[length:var(--fs-ui-body)]",
                                            children: [
                                                /*#__PURE__*/ _jsx("div", {
                                                    className: "font-medium",
                                                    children: g.label
                                                }),
                                                /*#__PURE__*/ _jsx("ul", {
                                                    className: "mt-1 space-y-0.5",
                                                    children: g.lines.map((l)=>/*#__PURE__*/ _jsxs("li", {
                                                            className: "flex gap-3",
                                                            children: [
                                                                /*#__PURE__*/ _jsxs("span", {
                                                                    className: "min-w-0 flex-1",
                                                                    children: [
                                                                        l.team,
                                                                        l.exercise ? /*#__PURE__*/ _jsxs("span", {
                                                                            className: "text-muted-foreground",
                                                                            children: [
                                                                                " · ",
                                                                                l.exercise
                                                                            ]
                                                                        }) : null
                                                                    ]
                                                                }),
                                                                /*#__PURE__*/ _jsxs("span", {
                                                                    className: "tabular-nums text-muted-foreground",
                                                                    children: [
                                                                        l.count,
                                                                        " ",
                                                                        l.count === 1 ? 'date' : 'dates'
                                                                    ]
                                                                }),
                                                                /*#__PURE__*/ _jsx("span", {
                                                                    className: "text-muted-foreground",
                                                                    children: l.summary
                                                                })
                                                            ]
                                                        }, l.key))
                                                })
                                            ]
                                        }, g.key)),
                                    assignments.filter((a)=>a.handling !== 'performed').map((a)=>/*#__PURE__*/ _jsxs("div", {
                                            className: "text-[length:var(--fs-ui-body)] text-muted-foreground",
                                            children: [
                                                "No dates for a SOW that",
                                                ' ',
                                                HANDLING_LABEL[a.handling]?.toLowerCase() ?? a.handling,
                                                " it",
                                                a.handling === 'routed' ? `: routed to ${a.routedToWorkstreamId ? lk.teamName(a.routedToWorkstreamId) : 'base scope'}` : a.notOwedReason ? `: ${NOT_OWED_REASON_LABEL[a.notOwedReason] ?? a.notOwedReason}` : '',
                                                /*#__PURE__*/ _jsx(CmSourceButton, {
                                                    locator: a.sourceLocator
                                                })
                                            ]
                                        }, a.id))
                                ]
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("section", {
                        className: "space-y-2",
                        children: [
                            /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                                variant: "group",
                                title: /*#__PURE__*/ _jsxs(_Fragment, {
                                    children: [
                                        "Dates · ",
                                        shown.length
                                    ]
                                })
                            }),
                            /*#__PURE__*/ _jsx(SpecraFilterToolbar, {
                                search: false,
                                filters: /*#__PURE__*/ _jsxs(_Fragment, {
                                    children: [
                                        /*#__PURE__*/ _jsxs(SpecraSelect, {
                                            "aria-label": "Team",
                                            size: "sm",
                                            className: "w-48",
                                            value: teamFilter,
                                            onChange: (e)=>setTeamFilter(e.target.value),
                                            "data-testid": Testids.cmDuty.workstreamFilter,
                                            children: [
                                                /*#__PURE__*/ _jsx("option", {
                                                    value: "",
                                                    children: "All teams"
                                                }),
                                                /*#__PURE__*/ _jsx("option", {
                                                    value: "contract-wide",
                                                    children: "Contract-wide"
                                                }),
                                                lk.workstreams.map((w)=>/*#__PURE__*/ _jsx("option", {
                                                        value: w.id,
                                                        children: w.name
                                                    }, w.id))
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsxs(SpecraSelect, {
                                            "aria-label": "Team family",
                                            size: "sm",
                                            className: "w-48",
                                            value: familyFilter,
                                            onChange: (e)=>setFamilyFilter(e.target.value),
                                            "data-testid": Testids.cmDuty.familyFilter,
                                            children: [
                                                /*#__PURE__*/ _jsx("option", {
                                                    value: "",
                                                    children: "Any family"
                                                }),
                                                lk.workstreams.map((w)=>/*#__PURE__*/ _jsxs("option", {
                                                        value: w.id,
                                                        children: [
                                                            w.name,
                                                            " family"
                                                        ]
                                                    }, w.id))
                                            ]
                                        })
                                    ]
                                }),
                                actions: access.canWrite ? /*#__PURE__*/ _jsx(SpecraButton, {
                                    size: "sm",
                                    variant: "secondary",
                                    onClick: ()=>setAddDate(true),
                                    "data-testid": Testids.cmDuty.addOneOff,
                                    children: "+ Add a date"
                                }) : undefined
                            }),
                            /*#__PURE__*/ _jsx(CmTable, {
                                caption: "Dates",
                                rows: shown,
                                columns: columns,
                                rowKey: (o)=>o.id,
                                testId: Testids.cmDuty.datesTable,
                                rowTestId: Testids.cmDuty.dateRow,
                                isLoading: dates.isLoading,
                                error: dates.error,
                                onRetry: ()=>void dates.refetch(),
                                onRowClick: (o)=>o.event?.projected ? undefined : params.open('occ', o.id),
                                getRowLabel: (o)=>`${d?.title ?? 'Deliverable'} due ${cmDate(o.dueOn)}`,
                                empty: teamFilter || familyFilter ? /*#__PURE__*/ _jsx(SpecraEmptyState, {
                                    size: "compact",
                                    title: "No dates for this team.",
                                    description: "The team filters hide every date of this deliverable."
                                }) : /*#__PURE__*/ _jsx(SpecraEmptyState, {
                                    size: "compact",
                                    title: "No dates yet.",
                                    description: "Dates appear once the rule has a start and its period or exercise is on the contract.",
                                    action: access.canWrite ? /*#__PURE__*/ _jsx(SpecraButton, {
                                        size: "inline",
                                        variant: "link",
                                        onClick: ()=>setAddDate(true),
                                        children: "Add a date by hand"
                                    }) : undefined
                                })
                            })
                        ]
                    })
                ]
            }),
            editRule ? /*#__PURE__*/ _jsx(RuleEditorDrawer, {
                programId: programId,
                dutyId: dutyId,
                rule: editRule === 'new' ? undefined : editRule,
                onClose: ()=>setEditRule(null),
                onSaved: (r)=>{
                    setEditRule(null);
                    setReceipt(r);
                }
            }) : null,
            editDuty && d ? /*#__PURE__*/ _jsx(DutyFormDrawer, {
                programId: programId,
                duty: d,
                onClose: ()=>setEditDuty(false),
                onSaved: ()=>setEditDuty(false)
            }) : null,
            addDate ? /*#__PURE__*/ _jsx(AddDateDrawer, {
                programId: programId,
                dutyId: dutyId,
                onClose: ()=>setAddDate(false)
            }) : null,
            /*#__PURE__*/ _jsx(ConfirmDeleteDialog, {
                open: confirmDelete,
                onOpenChange: setConfirmDelete,
                title: `Delete "${d?.title ?? 'this deliverable'}"?`,
                description: "Its dates are removed with it. A deliverable with a submitted date, or one the COR waived, can't be deleted: mark those dates not owed instead.",
                isPending: deleteDuty.isPending,
                onConfirm: ()=>deleteDuty.mutate({
                        dutyId
                    }, {
                        onSuccess: ()=>navigate(`/delivery/programs/${programId}/deliverables`),
                        onSettled: ()=>setConfirmDelete(false)
                    })
            }),
            logKind !== null ? /*#__PURE__*/ _jsx(LogEventDrawer, {
                programId: programId,
                kind: logKind,
                onClose: ()=>setLogKind(null)
            }) : null
        ]
    });
}
export function AddDateDrawer({ programId, dutyId, onClose }) {
    const lk = useCmLookups(programId);
    const add = useAddOccurrence(programId, {
        errors: 'inline',
        success: (r)=>`Date added: ${cmDate(r.occurrence?.dueOn)}. ${mintReceipt(r.result?.mint)}`
    });
    const [dueOn, setDueOn] = useState('');
    const [label, setLabel] = useState('');
    const [team, setTeam] = useState('');
    return /*#__PURE__*/ _jsx(CmDrawer, {
        title: "Add a date",
        description: "A one-off date for this deliverable. No rule schedules it, and no schedule change removes it.",
        onClose: onClose,
        busy: add.isPending,
        testId: Testids.cmDuty.addOneOffDrawer,
        footerProps: {
            confirmLabel: 'Add date',
            onConfirm: ()=>add.mutate({
                    dutyId,
                    dueOn,
                    label: label.trim(),
                    workstreamId: team
                }, {
                    onSuccess: onClose
                }),
            confirmDisabled: add.isPending,
            loading: add.isPending,
            onCancel: onClose,
            confirmProps: {
                'data-testid': Testids.cmDuty.addOneOffSave
            }
        },
        children: /*#__PURE__*/ _jsxs("div", {
            className: "space-y-3",
            children: [
                /*#__PURE__*/ _jsx(SpecraFormField, {
                    label: "Label",
                    hint: "Optional, e.g. “Kickoff briefing”",
                    children: /*#__PURE__*/ _jsx(SpecraInput, {
                        value: label,
                        onChange: (e)=>setLabel(e.target.value),
                        "data-testid": Testids.cmDuty.addOneOffLabel
                    })
                }),
                /*#__PURE__*/ _jsx(SpecraDateField, {
                    picker: "native",
                    label: "Due on",
                    hint: "Leave blank if the date is not stated yet.",
                    value: dueOn,
                    onValueChange: setDueOn,
                    "data-testid": Testids.cmDuty.addOneOffDate
                }),
                /*#__PURE__*/ _jsx(SpecraFormField, {
                    label: "Team",
                    hint: "Who owes it. Blank = contract-wide.",
                    children: /*#__PURE__*/ _jsxs(SpecraSelect, {
                        value: team,
                        onChange: (e)=>setTeam(e.target.value),
                        children: [
                            /*#__PURE__*/ _jsx("option", {
                                value: "",
                                children: "Contract-wide"
                            }),
                            lk.workstreams.map((w)=>/*#__PURE__*/ _jsx("option", {
                                    value: w.id,
                                    children: w.name
                                }, w.id))
                        ]
                    })
                }),
                add.error ? /*#__PURE__*/ _jsx(SpecraText, {
                    as: "p",
                    size: "caption",
                    tone: "danger",
                    role: "alert",
                    children: apiErrorMessage(add.error, 'Could not save')
                }) : null
            ]
        })
    });
}
